import ReactDOM from "react-dom";
import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Zap, CheckCircle, AlertCircle, Clock, Camera, Image } from "lucide-react";
import { toast } from "sonner";

export default function ShutdownsPage() {
  const { user } = useAuth();
  const [shutdowns, setShutdowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feeders, setFeeders] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    feeder_code: "", circle_name: user?.circle_name || "",
    start_datetime: "", end_datetime: "",
    je_name: "", je_contact: "", lineman_name: "",
    shutdown_board_photo_url: ""
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [shutRes, feedRes] = await Promise.all([
        api.get("/shutdowns"),
        api.get("/hierarchy/feeders")
      ]);
      setShutdowns(shutRes.data.items);
      setFeeders(feedRes.data);
    } catch { toast.error("Failed to load shutdowns"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.feeder_code) { toast.error("Please select a feeder"); return; }
    if (!form.start_datetime) { toast.error("Shutdown start date/time is required"); return; }
    if (!form.end_datetime) { toast.error("Shutdown end date/time is required"); return; }
    if (form.start_datetime >= form.end_datetime) { toast.error("End time must be after start time"); return; }
    setCreating(true);
    try {
      await api.post("/shutdowns", form);
      toast.success("Shutdown created");
      setShowCreate(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this shutdown?")) return;
    try {
      await api.delete(`/shutdowns/${id}`);
      toast.success("Shutdown cancelled");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel shutdown"); }
  };

  const isActive = (s) => {
    const now = new Date().toISOString();
    return s.start_datetime <= now && s.end_datetime >= now && s.status === "active";
  };

  const isUpcoming = (s) => s.start_datetime > new Date().toISOString() && s.status === "active";

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="shutdowns-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>Shutdown Management</h1>
          <p className="text-slate-500 text-sm">{shutdowns.filter(isActive).length} active · {shutdowns.filter(isUpcoming).length} upcoming</p>
        </div>
        {["admin", "ci", "supervisor"].includes(user?.role) && (
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600" data-testid="create-shutdown-btn">
            <Plus size={16} />Schedule Shutdown
          </button>
        )}
      </div>

      {/* Active Shutdowns Banner */}
      {shutdowns.filter(isActive).length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-sm p-3 mb-4 flex items-center gap-2">
          <Zap size={16} className="text-green-600" />
          <span className="text-sm font-semibold text-green-700">{shutdowns.filter(isActive).length} shutdown(s) currently active — DTC/MI operations possible</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
        <table className="dt-table">
          <thead>
            <tr><th>Feeder</th><th>Circle</th><th>Start</th><th>End</th><th>JE Name</th><th>Board Photo</th><th>Status</th>{["admin", "ci"].includes(user?.role) && <th>Action</th>}</tr>
          </thead>
          <tbody>
            {loading ? [...Array(5)].map((_, i) => <tr key={i}>{[...Array(8)].map((_, j) => <td key={j}><div className="h-4 bg-slate-100 rounded animate-pulse"></div></td>)}</tr>)
              : shutdowns.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-slate-400"><Zap size={32} className="mx-auto mb-2" />No shutdowns found</td></tr>
              ) : shutdowns.map(s => {
                const active = isActive(s);
                const upcoming = isUpcoming(s);
                return (
                  <tr key={s.id} className={active ? "bg-green-50/50" : ""}>
                    <td><span className="font-mono text-xs font-bold">{s.feeder_code}</span></td>
                    <td className="text-xs text-slate-600">{s.circle_name}</td>
                    <td className="text-xs text-slate-600">{new Date(s.start_datetime).toLocaleString("en-IN")}</td>
                    <td className="text-xs text-slate-600">{new Date(s.end_datetime).toLocaleString("en-IN")}</td>
                    <td className="text-xs text-slate-700">{s.je_name || "—"}<br /><span className="text-[10px] text-slate-400">{s.je_contact}</span></td>
                    <td>
                      {s.shutdown_board_photo_url ? (
                        <span className="flex items-center gap-1 text-green-600 text-[10px] font-semibold">
                          <Image size={12} />Captured
                        </span>
                      ) : (
                        <span className="text-slate-400 text-[10px]">—</span>
                      )}
                    </td>
                    <td>
                      <span className={`flex items-center gap-1 text-[10px] font-bold uppercase ${active ? "text-green-700" : upcoming ? "text-blue-700" : s.status === "cancelled" ? "text-red-500" : "text-slate-500"}`}>
                        {active ? <><Zap size={10} />ACTIVE</> : upcoming ? <><Clock size={10} />UPCOMING</> : s.status.toUpperCase()}
                      </span>
                    </td>
                    {["admin", "ci"].includes(user?.role) && (
                      <td>
                        {s.status !== "cancelled" && (
                          <button onClick={() => handleCancel(s.id)} className="text-xs text-red-500 hover:text-red-700 font-medium" data-testid={`cancel-shutdown-${s.id}`}>Cancel</button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] bg-black/60" onClick={() => setShowCreate(false)}>
          <div className="fixed top-12 left-1/2 -translate-x-1/2 w-full max-w-md bottom-4 bg-white rounded-sm shadow-xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 pt-5 pb-3 border-b border-slate-100 shrink-0">
              <h3 className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>Schedule Shutdown</h3>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
            <form id="shutdown-form" onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="field-label">Feeder *</label>
                <select className="field-select" required value={form.feeder_code} onChange={e => {
                  const f = feeders.find(fd => fd.feeder_code === e.target.value);
                  setForm({ ...form, feeder_code: e.target.value, circle_name: f?.circle_name || form.circle_name });
                }} data-testid="shutdown-feeder-select">
                  <option value="">Select feeder...</option>
                  {feeders.map(f => <option key={f.feeder_code} value={f.feeder_code}>{f.feeder_code} - {f.feeder_name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Start DateTime *</label>
                  <input type="datetime-local" className="field-input" required value={form.start_datetime} onChange={e => setForm({ ...form, start_datetime: e.target.value })} data-testid="shutdown-start" />
                </div>
                <div>
                  <label className="field-label">End DateTime *</label>
                  <input type="datetime-local" className="field-input" required value={form.end_datetime} onChange={e => setForm({ ...form, end_datetime: e.target.value })} data-testid="shutdown-end" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">JE Name</label>
                  <input className="field-input" value={form.je_name} onChange={e => setForm({ ...form, je_name: e.target.value })} data-testid="je-name" />
                </div>
                <div>
                  <label className="field-label">JE Contact</label>
                  <input className="field-input" value={form.je_contact} onChange={e => setForm({ ...form, je_contact: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Lineman Name</label>
                  <input className="field-input" value={form.lineman_name} onChange={e => setForm({ ...form, lineman_name: e.target.value })} />
                </div>
                <div>
                  <label className="field-label">Lineman Contact</label>
                  <input className="field-input" value={form.lineman_contact || ""} onChange={e => setForm({ ...form, lineman_contact: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="field-label">Shutdown Board Photo (at Discom)</label>
                <label className={`relative border-2 border-dashed rounded-sm p-4 text-center cursor-pointer transition-colors block ${
                  form.shutdown_board_photo_url 
                    ? "border-green-300 bg-green-50" 
                    : "border-slate-200 hover:border-orange-300 hover:bg-orange-50"
                }`}>
                  <input 
                    type="file" 
                    accept="image/*" 
                    capture="environment" 
                    className="hidden" 
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const mockUrl = `/mock/shutdowns/board_${Date.now()}_${file.name}`;
                        setForm({ ...form, shutdown_board_photo_url: mockUrl });
                        toast.success("Shutdown board photo captured (mock)");
                      }
                    }}
                    data-testid="shutdown-board-photo"
                  />
                  {form.shutdown_board_photo_url ? (
                    <div className="text-green-600 flex items-center justify-center gap-2">
                      <CheckCircle size={18} />
                      <span className="text-sm font-semibold">Photo Captured</span>
                    </div>
                  ) : (
                    <div className="text-slate-400 flex items-center justify-center gap-2">
                      <Camera size={18} />
                      <span className="text-sm">Tap to capture shutdown board</span>
                    </div>
                  )}
                </label>
                <p className="text-[10px] text-slate-400 mt-1">Take a photo of the shutdown board displayed at the DISCOM office</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600 disabled:opacity-60">
                  {creating ? "Creating..." : "Schedule"}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
